import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { coLearningWallBackdropProps } from '../ui/coLearningModalTheme';

/**
 * Premium onboarding / studio-access modal — matches gallery & activity modals.
 * Close (X) defaults to home page.
 */
export default function OnboardingModalShell({
  children,
  wide = false,
  title,
  eyebrow = 'Artmosphere Studio',
  onClose,
  showClose = true,
  closeOnBackdrop = true,
  ariaLabelledBy,
  zIndexClass = 'z-[200100]',
}) {
  const navigate = useNavigate();

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
      return;
    }
    navigate('/home', { replace: true });
  };

  const handleBackdropClick = () => {
    if (!closeOnBackdrop) return;
    handleClose();
  };

  const titleId = ariaLabelledBy || 'onboarding-modal-title';

  const modal = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 ${zIndexClass} flex items-center justify-center p-4 sm:p-6`}
      style={coLearningWallBackdropProps.style}
      role="presentation"
      onClick={handleBackdropClick}
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        className={`relative flex max-h-[min(92dvh,900px)] w-full flex-col overflow-hidden rounded-[28px] border border-[#e8d4bc]/90 bg-gradient-to-b from-[#fffdf9] to-[#fff6ec] shadow-[0_28px_80px_rgba(120,72,20,0.2)] ring-1 ring-[#efe2d0]/80 ${
          wide ? 'max-w-2xl' : 'max-w-xl'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-1 w-full shrink-0 bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F97316]"
          aria-hidden
        />

        {(title || showClose) && (
          <header className="relative shrink-0 border-b border-[#e8d4bc]/60 bg-[#FFFDF9]/95 px-4 py-3.5 backdrop-blur-md sm:px-6 sm:py-4">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-12 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,214,140,0.35),transparent_70%)]"
              aria-hidden
            />
            <div className="relative flex items-start justify-between gap-3">
              <div className="min-w-0 pr-2">
                {eyebrow ? (
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A7357]">
                    {eyebrow}
                  </p>
                ) : null}
                {title ? (
                  <h2
                    id={titleId}
                    className="font-serif text-xl font-semibold leading-tight text-[#2F251B] sm:text-2xl"
                  >
                    {title}
                  </h2>
                ) : null}
              </div>
              {showClose ? (
                <button
                  type="button"
                  onClick={handleClose}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E8C97A] bg-[#FFF9EF] text-[#5C4428] shadow-sm transition hover:border-[#F5CB5C] hover:bg-white hover:shadow-md"
                  aria-label="Close and return to home"
                >
                  <X className="h-4 w-4" strokeWidth={2.25} />
                </button>
              ) : null}
            </div>
          </header>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">{children}</div>
      </motion.div>
    </motion.div>
  );

  if (typeof document === 'undefined') {
    return modal;
  }

  return createPortal(modal, document.body);
}

/** Shared input + error styles for onboarding steps */
export const onboardingInputClass =
  'w-full min-h-[44px] rounded-xl border border-[#F5D99B]/70 bg-[#FFFDF9] px-3 text-sm text-[#2F251B] placeholder:text-[#9A7A55] shadow-sm transition focus:border-[#F5CB5C] focus:outline-none focus:ring-2 focus:ring-[#F5D99B]/40';

export const onboardingErrorClass =
  'rounded-xl border border-rose-200/80 bg-[#FFF9EF] px-3 py-2 text-sm text-rose-800';

/** Artist Wall upload wizard — matches onboarding / co-learning modals */
export const uploadWizardPanelClass =
  'rounded-[24px] border border-dashed border-[#F5D99B]/90 bg-gradient-to-b from-[#FFFDF9] to-[#FFF7ED] p-5 sm:p-6';

export const uploadWizardStepCardClass =
  'rounded-[24px] border border-[#F6D57B]/80 bg-gradient-to-b from-[#FFFDF9] to-[#FFF5E8] p-5 shadow-[0_12px_36px_rgba(120,72,20,0.08)] ring-1 ring-[#efe2d0]/60';

export const uploadWizardStepBadgeClass =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#F59E0B] to-[#F97316] text-sm font-bold text-white shadow-[0_4px_12px_rgba(234,88,12,0.25)]';

export const uploadWizardStepTitleClass = 'font-serif text-lg font-semibold text-[#2F251B]';

export const uploadWizardLabelClass =
  'mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#8A7357]';

export const uploadWizardBodyClass = 'text-sm leading-relaxed text-[#6B4E32]';

export const uploadWizardChoiceCardClass =
  'group overflow-hidden rounded-[24px] border-2 border-[#F6D57B]/80 bg-white text-left shadow-[0_10px_32px_rgba(120,72,20,0.08)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#F59E0B]/70 hover:shadow-[0_16px_40px_rgba(120,72,20,0.12)] focus:outline-none focus:ring-2 focus:ring-[#F5D99B]/50';

/** Artist profile create / edit — same museum palette */
export const profileStepCardClass = uploadWizardStepCardClass;

export const profileStyleChipActive =
  'rounded-full border border-[#F59E0B] bg-gradient-to-r from-[#F59E0B] to-[#F97316] px-4 py-2 text-xs font-semibold text-white shadow-[0_6px_16px_rgba(234,88,12,0.22)] sm:px-5 sm:py-2.5 sm:text-sm';

export const profileStyleChipIdle =
  'rounded-full border border-[#F5D99B] bg-[#FFFDF9] px-4 py-2 text-xs font-semibold text-[#5C4428] transition hover:border-[#F5CB5C] hover:bg-[#FFF9EF] sm:px-5 sm:py-2.5 sm:text-sm';
